import { t, tf } from "./i18n.js";
import { erzeugeAufnahmeEingabe } from "./aufnahme-eingabe.js";
// ==========================================
// SCORING-TRAINING (Open End)
//
// Einfach werfen und den Average sehen - kein Spielende, kein Gegner.
// Seit 14.08.2026 wird jede Aufnahme Dart fuer Dart geworfen (gemeinsamer
// Baustein) statt als Summe getippt. Die Anzeige haelt 3-Dart-Average,
// Darts, Gesamtpunkte, beste Aufnahme und 100+/140+/180 aktuell. Die
// Sitzung ueberlebt einen Reload (localStorage) und wird bewusst NICHT in
// Supabase gespeichert - es ist ein Uebungsmodus.
// ==========================================

const SCORING_STORAGE_KEY = "secure-darts:scoring-session";

// Aufnahmen, aelteste zuerst: { summe, darts }.
//
// FRUEHER standen hier nur die Summen. Mit der Dart-fuer-Dart-Eingabe ist
// die Dartzahl echt (wer nach zwei Darts aufhoert, hat zwei geworfen) - und
// ohne sie waere der 3-Dart-Average falsch. ladeScoringSitzung() nimmt
// gespeicherte Zahlen weiterhin an und rechnet sie mit drei Darts um, damit
// eine laufende Sitzung den Umbau ueberlebt.
let scoringVisits = [];

// --- Sitzung laden/speichern ---

function ladeScoringSitzung() {
  try {
    const roh = localStorage.getItem(SCORING_STORAGE_KEY);
    const daten = roh ? JSON.parse(roh) : null;
    scoringVisits = Array.isArray(daten) ? daten.map(normalisiereAufnahme).filter(Boolean) : [];
  } catch (e) {
    scoringVisits = [];
  }
}

/** Alte (Zahl) und neue Form ({summe, darts}) auf eine Form bringen. */
function normalisiereAufnahme(eintrag) {
  if (Number.isFinite(eintrag)) {
    return eintrag >= 0 && eintrag <= 180 ? { summe: eintrag, darts: 3 } : null;
  }
  if (!eintrag || typeof eintrag !== "object") return null;
  const summe = Number(eintrag.summe);
  const darts = Number(eintrag.darts);
  if (!Number.isFinite(summe) || summe < 0 || summe > 180) return null;
  return { summe, darts: Number.isFinite(darts) && darts > 0 ? darts : 3 };
}

function speichereScoringSitzung() {
  try {
    localStorage.setItem(SCORING_STORAGE_KEY, JSON.stringify(scoringVisits));
  } catch (e) {
    // Privater Modus: Sitzung gilt dann nur bis zum Reload
  }
}

// --- Einstieg / Ausstieg ---

function openScoringTraining() {
  currentAppMode = "scoring";
  ladeScoringSitzung();

  hideAllScreens();
  showScreenElement(document.getElementById("scoring-training-screen"));

  // ERST sichtbar, DANN bauen - createDartboard misst beim Zeichnen.
  scoringEingabe.baue();
  scoringEingabe.leere();
  scoringEingabe.aktualisiereSichtbarkeit();

  updateScoringUI();
}

function beendeScoringTraining() {
  // Nichts geht verloren (Sitzung liegt im Speicher) -> ohne Rueckfrage
  cancelCurrentGame("scoring-training-screen", true);
}

function resetScoringSitzung() {
  showConfirmModal(t("js.scoring.001"), () => {
    scoringVisits = [];
    speichereScoringSitzung();
    scoringEingabe.leere();
    updateScoringUI();
    showToast(t("js.scoring.002"), "success");
  });
}

// --- Eingabe ---

/** Eine Aufnahme uebernehmen.
 *  @returns {boolean} false = abgelehnt, die Darts bleiben stehen. */
function submitScoringVisit(presetScore = null, darts) {
  const score = Number(presetScore);
  if (!Number.isFinite(score)) return false;

  if (score > 180) {
    showToast(t("js.gotcha.004"), "warning");
    return false;
  }

  scoringVisits.push({
    summe: score,
    darts: Number.isFinite(darts) && darts > 0 ? darts : 3,
  });
  speichereScoringSitzung();
  updateScoringUI();
  return true;
}

function undoScoringVisit() {
  if (scoringVisits.length === 0) return;
  scoringVisits.pop();
  speichereScoringSitzung();
  scoringEingabe.leere();
  updateScoringUI();
}

// --- Anzeige ---

function scoringKennzahlen(visits) {
  const aufnahmen = visits.length;
  const gesamt = visits.reduce((a, v) => a + v.summe, 0);
  const darts = visits.reduce((a, v) => a + v.darts, 0);
  return {
    aufnahmen: aufnahmen,
    darts: darts,
    gesamt: gesamt,
    // 3-Dart-Average = Punkte je Dart mal drei. Vorher stand hier der
    // Schnitt der Aufnahme-Summen - dasselbe Ergebnis, SOLANGE jede
    // Aufnahme drei Darts hat. Mit echter Dartzahl stimmt das nicht mehr:
    // Zwei Darts fuer 100 sind ein Average von 150, nicht von 100.
    average: darts ? (gesamt / darts) * 3 : 0,
    beste: aufnahmen ? Math.max(...visits.map((v) => v.summe)) : 0,
    t100: visits.filter((v) => v.summe >= 100 && v.summe < 140).length,
    t140: visits.filter((v) => v.summe >= 140 && v.summe < 180).length,
    t180: visits.filter((v) => v.summe === 180).length,
  };
}

function updateScoringUI() {
  const k = scoringKennzahlen(scoringVisits);

  const setze = (id, wert) => {
    const el = document.getElementById(id);
    if (el) el.innerText = wert;
  };

  setze("scoring-average", k.aufnahmen ? k.average.toFixed(2) : "–");
  setze("scoring-darts", k.darts);
  setze("scoring-gesamt", k.gesamt);
  setze("scoring-beste", k.aufnahmen ? k.beste : "–");
  setze("scoring-100", k.t100);
  setze("scoring-140", k.t140);
  setze("scoring-180", k.t180);

  // Letzte Aufnahmen (neueste zuerst), reine Textliste
  const liste = document.getElementById("scoring-history");
  if (liste) {
    liste.innerHTML = "";
    scoringVisits
      .slice(-12)
      .reverse()
      .forEach((v, i) => {
        const zeile = document.createElement("div");
        zeile.style.cssText =
          "display:flex; justify-content:space-between; padding:6px 12px; border-bottom:1px solid var(--glass-border); font-size:0.9em;";
        const nr = document.createElement("span");
        nr.style.color = "var(--text-muted)";
        nr.textContent = `#${scoringVisits.length - i}`;
        const wert = document.createElement("span");
        wert.style.fontWeight = "800";
        wert.style.color =
          v.summe >= 140
            ? "var(--accent-red)"
            : v.summe >= 100
              ? "var(--accent-green)"
              : "var(--text-main)";
        // Bei einer unvollstaendigen Aufnahme steht die Dartzahl dabei -
        // sonst sieht "40" aus wie ein schwacher Wurf statt wie zwei Darts.
        wert.textContent = v.darts === 3 ? String(v.summe) : `${v.summe} (${v.darts})`;
        zeile.appendChild(nr);
        zeile.appendChild(wert);
        liste.appendChild(zeile);
      });
    if (scoringVisits.length === 0) {
      const leer = document.createElement("div");
      leer.style.cssText =
        "text-align:center; color:var(--text-muted); padding:14px;";
      leer.textContent = t("js.scoring.003");
      liste.appendChild(leer);
    }
  }

  const undoBtn = document.getElementById("btn-undo-scoring");
  if (undoBtn)
    undoBtn.style.display = scoringVisits.length > 0 ? "block" : "none";
}

// ---------------------------------------------------------------
// Bruecke zu window
// ---------------------------------------------------------------
// ---------------------------------------------------------------------------
// EINGABE (14.08.2026, Stufe 3)
//
// Auch die sechs Schnellwahlknoepfe (26/41/45/60/81/100) sind entfallen. Im
// Uebungsmodus ist gerade die Einzelfeldinformation der Zweck: Eine 60 sagt
// nicht, ob sie T20 war oder 20+20+20 - und genau das will man beim
// Scoring-Training wissen.
// ---------------------------------------------------------------------------
const scoringEingabe = erzeugeAufnahmeEingabe({
  modus: "scoring",
  ziel: () => document.getElementById("aufnahme-scoring"),
  aktiv: () =>
    document.getElementById("scoring-training-screen")?.style.display !== "none",
  onAufnahme: (wuerfe, summe) => submitScoringVisit(summe, wuerfe.length),
});

Object.assign(window, {
  openScoringTraining,
  beendeScoringTraining,
  resetScoringSitzung,
  submitScoringVisit,
  undoScoringVisit,
  scoringEingabe,
});
